import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack,
  Divider,
} from '@mui/material';
import {
  People,
  Email,
  EmojiEvents,
  TrendingUp,
  TrendingDown,
  Schedule,
  MonetizationOn,
  AccountBalance,
  BarChart,
  Visibility,
  Refresh,
  Analytics,
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { motion } from 'framer-motion';

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    contactMessages: 0,
    newUsersToday: 0,
    newMessagesToday: 0,
    verifiedUsers: 0,
    totalLeaderboardEntries: 0,
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Fetch total users
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;
      
      // Count verified users
      let verifiedUsers = 0;
      usersSnapshot.docs.forEach(doc => {
        if (doc.data().isVerified) verifiedUsers++;
      });

      // Fetch contact messages
      const messagesSnapshot = await getDocs(collection(db, 'contactMessages'));
      const contactMessages = messagesSnapshot.size;

      // Get recent users (last 5)
      const recentUsersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const recentUsersSnapshot = await getDocs(recentUsersQuery);
      const recentUsersData = recentUsersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
      }));

      // Get recent messages (last 5)
      const recentMessagesQuery = query(
        collection(db, 'contactMessages'),
        orderBy('timestamp', 'desc'),
        limit(5)
      );
      const recentMessagesSnapshot = await getDocs(recentMessagesQuery);
      const recentMessagesData = recentMessagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp)
      }));

      // Calculate today's stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let newUsersToday = 0;
      let newMessagesToday = 0;
      
      usersSnapshot.docs.forEach(doc => {
        const createdAt = doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt);
        if (createdAt >= today) newUsersToday++;
      });
      
      messagesSnapshot.docs.forEach(doc => {
        const timestamp = doc.data().timestamp?.toDate?.() || new Date(doc.data().timestamp);
        if (timestamp >= today) newMessagesToday++;
      });

      setStats({
        totalUsers,
        contactMessages,
        newUsersToday,
        newMessagesToday,
        verifiedUsers,
        totalLeaderboardEntries: 0, // This would come from leaderboard collection
      });
      
      setRecentUsers(recentUsersData);
      setRecentMessages(recentMessagesData);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, trend }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography color="textSecondary" gutterBottom variant="overline">
                {title}
              </Typography>
              <Typography variant="h4" component="div" fontWeight="bold">
                {loading ? '-' : value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="textSecondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Avatar sx={{ bgcolor: color, width: 56, height: 56 }}>
              {icon}
            </Avatar>
          </Box>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              {trend.direction === 'up' ? (
                <TrendingUp sx={{ color: 'success.main', mr: 1 }} />
              ) : (
                <TrendingDown sx={{ color: 'error.main', mr: 1 }} />
              )}
              <Typography variant="body2" color={trend.direction === 'up' ? 'success.main' : 'error.main'}>
                {trend.value}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Analytics Dashboard
        </Typography>
        <Tooltip title="Refresh Data">
          <IconButton onClick={fetchAnalyticsData} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            icon={<People />}
            color="primary.main"
            subtitle={`${stats.verifiedUsers} verified`}
            trend={{ direction: 'up', value: `+${stats.newUsersToday} today` }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Contact Messages"
            value={stats.contactMessages}
            icon={<Email />}
            color="info.main"
            subtitle="Total received"
            trend={{ direction: 'up', value: `+${stats.newMessagesToday} today` }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verified Users"
            value={stats.verifiedUsers}
            icon={<EmojiEvents />}
            color="success.main"
            subtitle={`${((stats.verifiedUsers / stats.totalUsers) * 100 || 0).toFixed(1)}% of total`}
          />
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3}>
        {/* Recent Users */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <People sx={{ mr: 1 }} />
              Recent User Registrations
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {loading ? (
              <LinearProgress />
            ) : (
              <Stack spacing={2}>
                {recentUsers.map((user, index) => (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {user.firstName?.[0] || user.email[0].toUpperCase()}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2">
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {user.email}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {user.createdAt?.toLocaleDateString()}
                        </Typography>
                      </Box>
                      {user.isVerified && (
                        <Chip label="Verified" size="small" color="success" />
                      )}
                    </Box>
                  </motion.div>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* Recent Contact Messages */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <Email sx={{ mr: 1 }} />
              Recent Contact Messages
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {loading ? (
              <LinearProgress />
            ) : (
              <Stack spacing={2}>
                {recentMessages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">
                          {message.name}
                        </Typography>
                        <Chip 
                          label={message.status || 'new'} 
                          size="small" 
                          color={message.status === 'resolved' ? 'success' : 'default'}
                        />
                      </Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {message.subject}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {message.timestamp?.toLocaleDateString()} • {message.email}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard; 